import type { BoundDataRow } from '../types/view-component.types';
import type { ColumnInfo, Row } from '../types/view-datasource.types';

const SEMANTIC_ALIASES: Record<string, string[]> = {
  content: ['content', 'body'],
  cover: ['cover', 'image', 'thumbnail'],
  date: ['date', 'publishedAt', 'updatedAt'],
  excerpt: ['excerpt', 'summary', 'description'],
  meta: ['meta', 'publishedAt', 'date'],
  subtitle: ['subtitle', 'description', 'summary'],
  thumbnail: ['thumbnail', 'image', 'cover'],
};

function toFieldAlias(name: string): string {
  const parts = name
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean);

  return parts
    .map((part, index) => {
      const normalized = part.toLowerCase();
      return index === 0
        ? normalized
        : normalized.charAt(0).toUpperCase() + normalized.slice(1);
    })
    .join('');
}

function readCell(row: Row, column: ColumnInfo): unknown {
  if (Object.prototype.hasOwnProperty.call(row.cells, column.id)) {
    return row.cells[column.id];
  }
  if (column.isPrimary) return row.cells._title ?? row.cells.title;
  return undefined;
}

function isTextLikeCell(value: unknown): value is { toString: () => string } {
  if (typeof value !== 'object' || value === null) return false;
  if (typeof (value as { toString?: unknown }).toString !== 'function') {
    return false;
  }
  if (typeof (value as { toDelta?: unknown }).toDelta === 'function') {
    return true;
  }
  return 'yText' in value || '_yText' in value || '_delta$' in value;
}

function normalizeCellValue(value: unknown): unknown {
  if (isTextLikeCell(value)) return value.toString();
  return value;
}

function setAliasIfPresent(
  target: BoundDataRow,
  alias: string,
  value: unknown
): void {
  if (!alias || value === undefined || target[alias] !== undefined) return;
  target[alias] = normalizeCellValue(value);
}

function applySemanticAliases(target: BoundDataRow): void {
  for (const [alias, candidates] of Object.entries(SEMANTIC_ALIASES)) {
    if (target[alias] !== undefined) continue;
    const value = candidates
      .map(candidate => target[candidate])
      .find(candidate => candidate !== undefined && candidate !== null);
    if (value !== undefined && value !== null) target[alias] = value;
  }
}

export function mapDatabaseRowToBoundDataRow(
  row: Row,
  columns: ReadonlyArray<ColumnInfo>
): BoundDataRow {
  const mapped: BoundDataRow = Object.fromEntries(
    Object.entries(row.cells).map(([key, value]) => [
      key,
      normalizeCellValue(value),
    ])
  );
  const orderedColumns = [...columns].sort(
    (a, b) => Number(Boolean(a.isPrimary)) - Number(Boolean(b.isPrimary))
  );

  for (const column of orderedColumns) {
    const value = readCell(row, column);
    setAliasIfPresent(mapped, column.name, value);
    setAliasIfPresent(mapped, toFieldAlias(column.name), value);
    if (column.isPrimary) setAliasIfPresent(mapped, 'title', value);
  }

  // The structural row title and the authored content title can coexist.
  // Resolve the semantic title explicitly, independent of physical IDs and
  // column order. An intentionally empty authored title must remain empty.
  const contentTitle = columns.find(
    column =>
      !column.isPrimary &&
      column.type !== 'title' &&
      toFieldAlias(column.name) === 'title'
  );
  if (contentTitle) {
    const value = readCell(row, contentTitle);
    if (value !== undefined) mapped.title = normalizeCellValue(value);
  }

  applySemanticAliases(mapped);
  return mapped;
}

export function mapDatabaseRowsToBoundDataRows(
  rows: ReadonlyArray<Row>,
  columns: ReadonlyArray<ColumnInfo>
): BoundDataRow[] {
  return rows.map(row => mapDatabaseRowToBoundDataRow(row, columns));
}
